import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const faqs = [
  {
    question: "What if my client can't hear the assistant's voice?",
    answer: (
      <>
        <p className="mb-4">When clients are talking the assistant, if they don't hear it say anything, have them check the screen to see if what they are saying is being picked up and written back. If not, or if they do see it but they want to hear it, swipe up on the app to close it, then open it again. This sometimes happens if the audio source switches between the phone, AirPods, a car, or any other Bluetooth device. It can also happen if they toggle off the microphone button which is a handy way to pause the app from listening.</p>
        <p>They can also hit the speaker button and turn off the sound. This can be useful if they don't want hear but only see when the assistant is providing a short recap of what the client said.</p>
      </>
    ),
    plainTextAnswer: "When clients are talking the assistant, if they don't hear it say anything, have them check the screen to see if what they are saying is being picked up and written back. If not, or if they do see it but they want to hear it, swipe up on the app to close it, then open it again. This sometimes happens if the audio source switches between the phone, AirPods, a car, or any other Bluetooth device. It can also happen if they toggle off the microphone button which is a handy way to pause the app from listening. They can also hit the speaker button and turn off the sound. This can be useful if they don't want hear but only see when the assistant is providing a short recap of what the client said."
  },
  {
    question: "What if the client's screen goes dark?",
    answer: "While you making a contact, there is an icon that looks like an \"eye\". Tap that and it will enable a feature called Wake Lock which keeps the screen from dimming or locking after the normal screen timeout."
  },
  {
    question: "What if I or my clients can't find the emails?",
    answer: "Check junk mail, and then mark it as not junk. You can also add the domain @lifecoachassist.ai to your safe senders list."
  },
  {
    question: "How is the information stored?",
    answer: "In our semantic storage system, each memory exists across a universe of trillions and trillions of relational positions rather than at a single point. Mirroring human memory, information is organized around meaning and associations, enabling recall based on intent rather than exact matches within a space of meaning."
  },
  {
    question: "Is the Life Coach Assist app secure?",
    answer: "It uses the same kinds of security protections as most modern websites. All communication is encrypted with SSL, a foundational, legacy cryptographic protocol. When payments are involved, they’re handled by a reputable third-party provider, and that information is not stored in our system. We will never sell email addresses or any information shared with us."
  }
];

const FAQItem = ({ faq, index, isOpen, toggleOpen }) => {
  return (
    <div className="border-b border-gray-200 last:border-0">
      <button
        className="w-full py-6 flex items-center justify-between text-left focus:outline-none group"
        onClick={toggleOpen}
      >
        <div className="flex items-start gap-4 sm:gap-6">
          <span className="text-gray-400 text-xl">
            {String(index + 1)}
          </span>
          <span className="text-xl font-medium text-gray-900 group-hover:text-gray-600 transition-colors">
            {faq.question}
          </span>
        </div>
        <div className={`flex-shrink-0 ml-4 w-8 h-8 rounded-full flex items-center justify-center transition-colors duration-200 ${isOpen ? 'bg-[#FCF171] text-gray-900' : 'bg-gray-100 text-gray-600'}`}>
          {isOpen ? (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
            </svg>
          ) : (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          )}
        </div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="pb-8 pl-0 sm:pl-14 pr-4 text-gray-600 leading-relaxed text-lg">
              {faq.answer}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(0);

  const toggleOpen = (index) => {
    setOpenIndex(openIndex === index ? -1 : index);
  };

  // Schema.org Structured Data
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.plainTextAnswer || faq.answer
      }
    }))
  };

  return (
    <section className="py-20 bg-white border-b border-gray-100">
      <script type="application/ld+json">
        {JSON.stringify(schemaData)}
      </script>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-4xl font-bold text-center text-black mb-16 font-mono">
          FAQ’s
        </h2>
        <div className="bg-gray-50 rounded-2xl p-6 sm:p-8 shadow-xl border border-gray-100">
          {faqs.map((faq, index) => (
            <FAQItem
              key={index}
              faq={faq}
              index={index}
              isOpen={openIndex === index}
              toggleOpen={() => toggleOpen(index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
